/**
 * Instantiates a raw HTML string (imported via Vite's `?raw` loader) into a
 * standalone `DocumentFragment`. Parsing goes through a `<template>` element
 * so the content is inert (no scripts execute, no images fetch) until it is
 * attached to the document — the same trick native `<template>` usage relies on.
 *
 * The `rawHtml` argument must always be developer-authored, static markup
 * (a template file or SVG asset) — never API/user data. Dynamic data is
 * layered on afterwards via `fillSlot`, which only accepts DOM nodes built
 * with `createElement`/`textContent`.
 */
export function instantiate(rawHtml: string): DocumentFragment {
    const wrapper = document.createElement('template');
    wrapper.innerHTML = rawHtml.trim();
    return wrapper.content.cloneNode(true) as DocumentFragment;
}

/**
 * Replaces a named `<slot name="...">` placeholder with real content. Slots
 * here are light-DOM query hooks (not Shadow DOM slot projection) — the
 * `<slot>` element itself is removed and swapped for the given node(s), so
 * classes on parent wrappers (which drive styling) are left untouched.
 *
 * Passing an empty array removes the slot with no replacement, matching the
 * legacy `ss-hidden` treatment of empty/absent alert fields.
 */
export function fillSlot(root: ParentNode, name: string, content: string | Node | Node[]): void {
    const slot = root.querySelector(`slot[name="${name}"]`);
    if (!slot) {
        return;
    }

    if (typeof content === 'string') {
        slot.replaceWith(document.createTextNode(content));
        return;
    }

    if (Array.isArray(content)) {
        if (content.length === 0) {
            slot.remove();
            return;
        }
        slot.replaceWith(...content);
        return;
    }

    slot.replaceWith(content);
}

/** Removes a named slot placeholder without replacement (e.g. no CTA link). */
export function clearSlot(root: ParentNode, name: string): void {
    root.querySelector(`slot[name="${name}"]`)?.remove();
}
